export type TriageViewMode = 'grid' | 'list';
export type TriageDensity = 'comfortable' | 'compact';

export type TriageColumnId =
    | 'slug'
    | 'priority'
    | 'title'
    | 'requester'
    | 'project'
    | 'type'
    | 'updatedAt'
    | 'actions';

export type TriageSort = {
    column: TriageColumnId;
    direction: 'asc' | 'desc';
};

export type TriagePageSize = 5 | 10 | 25 | 50 | 100;

export type TriageViewPrefs = {
    mode: TriageViewMode;
    density: TriageDensity;
    /** Ordered visible columns (actions should remain last when present). */
    columns: TriageColumnId[];
    sort: TriageSort;
    /** Poll the triage queue on an interval when true. */
    autoRefresh: boolean;
    /** Cards/rows per page (client-side, grid and list). */
    pageSize: TriagePageSize;
    /** Show SPF/DKIM/DMARC badges in the dense list title cell. */
    showEmailHeaders: boolean;
};

export const TRIAGE_AUTO_REFRESH_MS = 30_000;

export const TRIAGE_PAGE_SIZE_OPTIONS: TriagePageSize[] = [5, 10, 25, 50, 100];

export const TRIAGE_ALL_COLUMNS: TriageColumnId[] = [
    'slug',
    'priority',
    'title',
    'requester',
    'project',
    'type',
    'updatedAt',
    'actions',
];

/** Optional columns offered in the column picker (slug + RT metadata). */
export const TRIAGE_CONFIGURABLE_COLUMNS: TriageColumnId[] = [
    'slug',
    'priority',
    'requester',
    'project',
    'type',
    'updatedAt',
];

/** Default visible columns for the dense list (RT-like; priority toggled on in picker). */
export const TRIAGE_DEFAULT_LIST_COLUMNS: TriageColumnId[] = [
    'slug',
    'title',
    'requester',
    'project',
    'type',
    'updatedAt',
    'actions',
];

export const TRIAGE_SORTABLE_COLUMNS: TriageColumnId[] = [
    'slug',
    'priority',
    'title',
    'requester',
    'project',
    'type',
    'updatedAt',
];

const STORAGE_KEY = 'yoda.triage.viewPrefs.v2';
const LEGACY_STORAGE_KEY = 'yoda.triage.viewPrefs';

export const DEFAULT_TRIAGE_VIEW_PREFS: TriageViewPrefs = {
    mode: 'grid',
    density: 'compact',
    columns: [...TRIAGE_DEFAULT_LIST_COLUMNS],
    sort: { column: 'priority', direction: 'desc' },
    autoRefresh: false,
    pageSize: 25,
    showEmailHeaders: false,
};

const isPageSize = (value: unknown): value is TriagePageSize =>
    typeof value === 'number' && (TRIAGE_PAGE_SIZE_OPTIONS as number[]).includes(value);

const normalizePageSize = (value: unknown): TriagePageSize =>
    isPageSize(value) ? value : DEFAULT_TRIAGE_VIEW_PREFS.pageSize;

/** Tab title with queue size first so browsers keep the count visible. */
export function formatTriageDocumentTitle(count: number, pageLabel: string): string {
    return `${count} · ${pageLabel} | Yoda`;
}

const isViewMode = (value: unknown): value is TriageViewMode =>
    value === 'grid' || value === 'list';

const isDensity = (value: unknown): value is TriageDensity =>
    value === 'comfortable' || value === 'compact';

const isColumnId = (value: unknown): value is TriageColumnId =>
    typeof value === 'string' && (TRIAGE_ALL_COLUMNS as string[]).includes(value);

const normalizeColumns = (columns: unknown): TriageColumnId[] => {
    if (!Array.isArray(columns))
        return [...DEFAULT_TRIAGE_VIEW_PREFS.columns];

    const seen = new Set<TriageColumnId>();
    const ordered: TriageColumnId[] = [];
    for (const item of columns) {
        if (!isColumnId(item) || seen.has(item))
            continue;
        seen.add(item);
        ordered.push(item);
    }

    if (!ordered.length)
        return [...DEFAULT_TRIAGE_VIEW_PREFS.columns];

    // Title and actions are always visible in list mode.
    if (!ordered.includes('title'))
        ordered.splice(Math.min(1, ordered.length), 0, 'title');
    if (!ordered.includes('actions'))
        ordered.push('actions');

    // Keep actions last when visible.
    if (ordered.includes('actions')) {
        const without = ordered.filter((id) => id !== 'actions');
        return [...without, 'actions'];
    }
    return ordered;
};

const normalizeSort = (sort: unknown): TriageSort => {
    if (!sort || typeof sort !== 'object')
        return { ...DEFAULT_TRIAGE_VIEW_PREFS.sort };

    const column = (sort as TriageSort).column;
    const direction = (sort as TriageSort).direction;
    if (!isColumnId(column) || !TRIAGE_SORTABLE_COLUMNS.includes(column))
        return { ...DEFAULT_TRIAGE_VIEW_PREFS.sort };
    if (direction !== 'asc' && direction !== 'desc')
        return { ...DEFAULT_TRIAGE_VIEW_PREFS.sort };

    return { column, direction };
};

export function loadTriageViewPrefs(): TriageViewPrefs {
    try {
        let raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            raw = localStorage.getItem(LEGACY_STORAGE_KEY);
        if (!raw) {
            return {
                ...DEFAULT_TRIAGE_VIEW_PREFS,
                columns: [...DEFAULT_TRIAGE_VIEW_PREFS.columns],
                sort: { ...DEFAULT_TRIAGE_VIEW_PREFS.sort },
            };
        }

        const parsed = JSON.parse(raw);
        return {
            mode: isViewMode(parsed?.mode) ? parsed.mode : DEFAULT_TRIAGE_VIEW_PREFS.mode,
            density: isDensity(parsed?.density) ? parsed.density : DEFAULT_TRIAGE_VIEW_PREFS.density,
            columns: normalizeColumns(parsed?.columns),
            sort: normalizeSort(parsed?.sort),
            autoRefresh: parsed?.autoRefresh === true,
            pageSize: normalizePageSize(parsed?.pageSize),
            showEmailHeaders: parsed?.showEmailHeaders === true,
        };
    } catch {
        return {
            ...DEFAULT_TRIAGE_VIEW_PREFS,
            columns: [...DEFAULT_TRIAGE_VIEW_PREFS.columns],
            sort: { ...DEFAULT_TRIAGE_VIEW_PREFS.sort },
        };
    }
}

export function saveTriageViewPrefs(prefs: TriageViewPrefs): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            mode: prefs.mode,
            density: prefs.density,
            columns: normalizeColumns(prefs.columns),
            sort: normalizeSort(prefs.sort),
            autoRefresh: prefs.autoRefresh === true,
            pageSize: normalizePageSize(prefs.pageSize),
            showEmailHeaders: prefs.showEmailHeaders === true,
        }));
    } catch {
        // Ignore quota / private mode failures.
    }
}

export function toggleColumnVisibility(
    columns: TriageColumnId[],
    columnId: TriageColumnId,
    visible: boolean
): TriageColumnId[] {
    if (columnId === 'title' || columnId === 'actions')
        return normalizeColumns(columns);

    if (visible) {
        if (columns.includes(columnId))
            return normalizeColumns(columns);
        const next = [...columns.filter((id) => id !== 'actions'), columnId];
        if (columns.includes('actions'))
            next.push('actions');
        return normalizeColumns(next);
    }

    return normalizeColumns(columns.filter((id) => id !== columnId));
}

export function moveColumn(
    columns: TriageColumnId[],
    columnId: TriageColumnId,
    direction: 'up' | 'down'
): TriageColumnId[] {
    const ordered = normalizeColumns(columns);
    const index = ordered.indexOf(columnId);
    if (index < 0)
        return ordered;

    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= ordered.length)
        return ordered;

    // Do not move actions away from last position.
    if (ordered[index] === 'actions' || ordered[swapWith] === 'actions')
        return ordered;

    const next = [...ordered];
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    return normalizeColumns(next);
}
